import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { 
  User, 
  Building, 
  Bell, 
  Shield, 
  Palette,
  Globe,
  Save,
  Mail,
  Calendar,
  Clock,
  Check,
  X,
  CreditCard
} from 'lucide-react'

import toast from 'react-hot-toast'

import { Navigation } from '../../components/Navigation'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { useAuth } from '../../hooks/useAuth'
import { db } from '../../lib/supabase'
import { SubscriptionSettings } from './SubscriptionSettings'

const profileSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
})

const companySchema = z.object({
  companyName: z.string().min(2, 'Company name must be at least 2 characters'),
  industry: z.string().optional(),
  timezone: z.string().optional(),
})

const passwordSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
})

type ProfileFormData = z.infer<typeof profileSchema>
type CompanyFormData = z.infer<typeof companySchema>
type PasswordFormData = z.infer<typeof passwordSchema>

export const Settings: React.FC = () => {
  const { appUser, refreshUser } = useAuth()
  const [activeTab, setActiveTab] = useState('profile')
  const [notifications, setNotifications] = useState({
    email: true,
    alerts: true,
    reports: false,
    reminders: true
  })
  const [preferences, setPreferences] = useState({
    frequency: 'weekly',
    sendTime: '09:00',
    language: 'en',
    timezone: 'UTC'
  })

  const profileForm = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
    }
  })

  const companyForm = useForm<CompanyFormData>({
    resolver: zodResolver(companySchema),
    defaultValues: {
      companyName: '',
      industry: '',
      timezone: 'UTC'
    }
  })

  const passwordForm = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
  })

  useEffect(() => {
    // Load user data when component mounts
    if (appUser) {
      // Set default values based on user data
      profileForm.setValue('firstName', appUser.email?.split('@')[0] || '')
      profileForm.setValue('lastName', '')
    }
  }, [appUser, profileForm])

  const tabs = [
    { id: 'profile', name: 'Profile', icon: User },
    { id: 'company', name: 'Company', icon: Building },
    { id: 'subscription', name: 'Subscription', icon: CreditCard },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'security', name: 'Security', icon: Shield },
    { id: 'preferences', name: 'Preferences', icon: Palette }
  ]

  const handleProfileSave = async (data: ProfileFormData) => {
    try {
      toast.success('Profile updated successfully')
      console.log('Profile data:', data)
      // TODO: Implement actual profile update
    } catch (error) {
      toast.error('Failed to update profile')
    }
  }

  const handleCompanySave = async (data: CompanyFormData) => {
    try {
      toast.success('Company settings updated successfully')
      console.log('Company data:', data)
      // TODO: Implement actual company update
    } catch (error) {
      toast.error('Failed to update company settings')
    }
  }

  const handlePasswordChange = async (data: PasswordFormData) => {
    try {
      toast.success('Password updated successfully')
      passwordForm.reset()
      // TODO: Implement actual password change
    } catch (error) {
      toast.error('Failed to update password')
    }
  }

  const handleNotificationToggle = (key: string) => {
    setNotifications(prev => ({
      ...prev,
      [key]: !prev[key as keyof typeof prev]
    }))
    toast.success('Notification preferences updated')
  }

  const handlePreferenceChange = (key: string, value: string) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }))
    toast.success('Preferences updated')
  }

  const handleTestNotification = () => {
    toast.success('Test notification sent! Check your email.')
  }

  const handleExportData = () => {
    const userData = {
      profile: appUser,
      notifications,
      preferences,
      exportedAt: new Date().toISOString()
    }

    const blob = new Blob([JSON.stringify(userData, null, 2)], { type: 'application/json' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `mentra-data-export-${new Date().toISOString().split('T')[0]}.json`
    a.click()
    window.URL.revokeObjectURL(url)
    
    toast.success('Data exported successfully')
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Profile Information</h3>
              <form onSubmit={profileForm.handleSubmit(handleProfileSave)} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Email"
                    value={appUser?.email || ''}
                    disabled
                    helperText="Contact support to change your email"
                    className="bg-slate-50"
                  />
                  <Input
                    label="Role"
                    value={appUser?.role || ''}
                    disabled
                    className="bg-slate-50"
                  />
                  <Input
                    label="First Name"
                    {...profileForm.register('firstName')}
                    error={profileForm.formState.errors.firstName?.message}
                    placeholder="Enter your first name"
                    className="border-slate-200 focus:border-sage-500 focus:ring-sage-500/20"
                  />
                  <Input
                    label="Last Name"
                    {...profileForm.register('lastName')}
                    error={profileForm.formState.errors.lastName?.message}
                    placeholder="Enter your last name"
                    className="border-slate-200 focus:border-sage-500 focus:ring-sage-500/20"
                  />
                </div>
                <div className="flex justify-end">
                  <Button type="submit" className="bg-sage-600 hover:bg-sage-700 text-white">
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )

      case 'company':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Company Settings</h3>
              <form onSubmit={companyForm.handleSubmit(handleCompanySave)} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Company Name"
                    {...companyForm.register('companyName')}
                    error={companyForm.formState.errors.companyName?.message}
                    placeholder="Your company name"
                    className="border-slate-200 focus:border-sage-500 focus:ring-sage-500/20"
                  />
                  <Input
                    label="Industry"
                    {...companyForm.register('industry')}
                    error={companyForm.formState.errors.industry?.message}
                    placeholder="e.g., Technology, Healthcare"
                    className="border-slate-200 focus:border-sage-500 focus:ring-sage-500/20"
                  />
                  <div>
                    <label className="label">Company Size</label>
                    <select 
                      className="input border-slate-200 focus:border-sage-500 focus:ring-sage-500/20"
                      onChange={(e) => toast.success(`Company size updated to ${e.target.value}`)}
                    >
                      <option value="">Select size</option>
                      <option value="1-10">1-10 employees</option>
                      <option value="11-50">11-50 employees</option>
                      <option value="51-200">51-200 employees</option>
                      <option value="201-1000">201-1000 employees</option>
                      <option value="1000+">1000+ employees</option>
                    </select>
                  </div>
                  <div>
                    <label className="label">Time Zone</label>
                    <select 
                      {...companyForm.register('timezone')}
                      className="input border-slate-200 focus:border-sage-500 focus:ring-sage-500/20"
                    >
                      <option value="UTC">UTC</option>
                      <option value="EST">Eastern Time</option>
                      <option value="PST">Pacific Time</option>
                      <option value="GMT">Greenwich Mean Time</option>
                    </select>
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button type="submit" className="bg-sage-600 hover:bg-sage-700 text-white">
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )

      case 'subscription':
        return <SubscriptionSettings />

      case 'notifications':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Notification Preferences</h3>
              <div className="space-y-4">
                {Object.entries(notifications).map(([key, enabled]) => {
                  const configs = {
                    email: { icon: Mail, title: 'Email Notifications', desc: 'Receive updates via email' },
                    alerts: { icon: Bell, title: 'Care Alerts', desc: 'Get notified of urgent wellness alerts' },
                    reports: { icon: Calendar, title: 'Weekly Reports', desc: 'Receive weekly summary reports' },
                    reminders: { icon: Clock, title: 'Check-in Reminders', desc: 'Reminders for team check-ins' }
                  }
                  const config = configs[key as keyof typeof configs]
                  
                  return (
                    <Card key={key} className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <config.icon className="w-5 h-5 text-sage-500 mr-3" />
                          <div>
                            <p className="font-medium text-slate-900">{config.title}</p>
                            <p className="text-sm text-slate-600">{config.desc}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleNotificationToggle(key)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            enabled ? 'bg-sage-600' : 'bg-slate-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              enabled ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    </Card>
                  )
                })}
                
                <div className="pt-4">
                  <Button 
                    onClick={handleTestNotification}
                    variant="outline"
                    className="border-slate-300 text-slate-700 hover:bg-slate-50"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Send Test Notification
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )

      case 'security':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Security Settings</h3>
              <div className="space-y-6">
                <Card className="p-6">
                  <h4 className="font-medium text-slate-900 mb-2">Change Password</h4>
                  <p className="text-sm text-slate-600 mb-4">
                    Update your password to keep your account secure
                  </p>
                  <form onSubmit={passwordForm.handleSubmit(handlePasswordChange)} className="space-y-4">
                    <Input
                      label="Current Password"
                      type="password"
                      {...passwordForm.register('currentPassword')}
                      error={passwordForm.formState.errors.currentPassword?.message}
                      placeholder="Enter current password"
                      className="border-slate-200 focus:border-sage-500 focus:ring-sage-500/20"
                    />
                    <Input
                      label="New Password"
                      type="password"
                      {...passwordForm.register('newPassword')}
                      error={passwordForm.formState.errors.newPassword?.message}
                      placeholder="Enter new password"
                      className="border-slate-200 focus:border-sage-500 focus:ring-sage-500/20"
                    />
                    <Input
                      label="Confirm New Password"
                      type="password"
                      {...passwordForm.register('confirmPassword')}
                      error={passwordForm.formState.errors.confirmPassword?.message}
                      placeholder="Confirm new password"
                      className="border-slate-200 focus:border-sage-500 focus:ring-sage-500/20"
                    />
                    <Button type="submit" className="bg-sage-600 hover:bg-sage-700 text-white">
                      Update Password
                    </Button>
                  </form>
                </Card>

                <Card className="p-6">
                  <h4 className="font-medium text-slate-900 mb-2">Two-Factor Authentication</h4>
                  <p className="text-sm text-slate-600 mb-4">
                    Add an extra layer of security to your account
                  </p>
                  <Button 
                    variant="outline" 
                    onClick={() => toast.success('2FA setup coming in Phase 2')}
                    className="border-slate-300 text-slate-700 hover:bg-slate-50"
                  >
                    Enable 2FA
                  </Button>
                </Card>

                <Card className="p-6">
                  <h4 className="font-medium text-slate-900 mb-2">Data Export</h4>
                  <p className="text-sm text-slate-600 mb-4">
                    Download a copy of your data
                  </p>
                  <Button 
                    variant="outline" 
                    onClick={handleExportData}
                    className="border-slate-300 text-slate-700 hover:bg-slate-50"
                  >
                    Export Data
                  </Button>
                </Card>
              </div>
            </div>
          </div>
        )

      case 'preferences':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Wellness Preferences</h3>
              <div className="space-y-6">
                <Card className="p-6">
                  <h4 className="font-medium text-slate-900 mb-4">Assessment Schedule</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="label">Frequency</label>
                      <select 
                        value={preferences.frequency}
                        onChange={(e) => handlePreferenceChange('frequency', e.target.value)}
                        className="input border-slate-200 focus:border-sage-500 focus:ring-sage-500/20"
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="biweekly">Bi-weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                    <div>
                      <label className="label">Send Time</label>
                      <select 
                        value={preferences.sendTime}
                        onChange={(e) => handlePreferenceChange('sendTime', e.target.value)}
                        className="input border-slate-200 focus:border-sage-500 focus:ring-sage-500/20"
                      >
                        <option value="09:00">9:00 AM</option>
                        <option value="12:00">12:00 PM</option>
                        <option value="15:00">3:00 PM</option>
                        <option value="18:00">6:00 PM</option>
                      </select>
                    </div>
                  </div>
                </Card>

                <Card className="p-6">
                  <h4 className="font-medium text-slate-900 mb-4">Language & Region</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="label">Language</label>
                      <select 
                        value={preferences.language}
                        onChange={(e) => handlePreferenceChange('language', e.target.value)}
                        className="input border-slate-200 focus:border-sage-500 focus:ring-sage-500/20"
                      >
                        <option value="en">English</option>
                        <option value="ar">العربية</option>
                        <option value="es">Español</option>
                        <option value="fr">Français</option>
                      </select>
                    </div>
                    <div>
                      <label className="label">Time Zone</label>
                      <select 
                        value={preferences.timezone}
                        onChange={(e) => handlePreferenceChange('timezone', e.target.value)}
                        className="input border-slate-200 focus:border-sage-500 focus:ring-sage-500/20"
                      >
                        <option value="UTC">UTC</option>
                        <option value="EST">Eastern Time</option>
                        <option value="PST">Pacific Time</option>
                        <option value="GMT">Greenwich Mean Time</option>
                      </select>
                    </div>
                  </div>
                </Card>

                <div className="flex justify-end">
                  <Button 
                    onClick={() => toast.success('Preferences saved successfully')}
                    className="bg-sage-600 hover:bg-sage-700 text-white"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save Preferences
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-calming">
      <Navigation variant="dashboard" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Settings</h1>
          <p className="text-slate-600">
            Manage your account, company, and wellness preferences
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card className="p-0">
              <nav className="space-y-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center px-4 py-3 text-left transition-colors rounded-lg ${
                      activeTab === tab.id
                        ? 'bg-sage-100 text-sage-700 border-r-2 border-sage-500'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                  >
                    <tab.icon className="w-5 h-5 mr-3" />
                    {tab.name}
                  </button>
                ))}
              </nav>
            </Card>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            <Card className="p-8">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                {renderTabContent()}
              </motion.div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}